import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  LinearProgress,
  useTheme,
  alpha,
  Skeleton,
} from '@mui/material';
import {
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  AttachMoney as MoneyIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as ActiveIcon,
  Schedule as TrialIcon,
  Block as SuspendedIcon,
  CalendarToday as CalendarIcon,
  ArrowUpward,
  ArrowDownward,
  Domain,
  AccountTree,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface PlatformStats {
  platform: {
    totalTenants: number;
    activeTenants: number;
    trialTenants: number;
    suspendedTenants: number;
    totalMRR: number;
    averageTenantValue: number;
  };
  aggregate: {
    totalProperties: number;
    totalUnits: number;
    totalReservations: number;
    totalUsers: number;
    totalRevenue: number;
    monthlyRevenue: number;
    averageOccupancy: number;
  };
  recentTenants: Array<{
    _id: string;
    name: string;
    status: string;
    createdAt: string;
  }>;
  lastUpdated: string;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  trend?: number;
  chips?: React.ReactNode;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, color, subtitle, trend, chips }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="600" sx={{ mb: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {chips && (
              <Box sx={{ mt: 2 }}>
                {chips}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
        {trend !== undefined && (
          <Box display="flex" alignItems="center" mt={2}>
            {trend >= 0 ? (
              <ArrowUpward sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
            ) : (
              <ArrowDownward sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
            )}
            <Typography 
              variant="caption" 
              color={trend >= 0 ? 'success.main' : 'error.main'}
              fontWeight="500"
            >
              {Math.abs(trend)}% from last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const PlatformDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<PlatformStats | null>(null);

  useEffect(() => {
    if (user?.isPlatformAdmin) {
      fetchPlatformStats();
    }
  }, [user]);

  const fetchPlatformStats = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/platform/tenants/dashboard');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err: any) {
      console.error('Error fetching platform stats:', err);
      setError(err.response?.data?.error || 'Failed to load platform statistics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <ActiveIcon sx={{ fontSize: 20 }} />;
      case 'trial':
        return <TrialIcon sx={{ fontSize: 20 }} />;
      case 'suspended':
        return <SuspendedIcon sx={{ fontSize: 20 }} />;
      default:
        return <BusinessIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
      active: 'success',
      trial: 'info',
      suspended: 'error',
      cancelled: 'default',
    };
    return colors[status] || 'default';
  };

  if (!user?.isPlatformAdmin) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          Access denied. Platform admin privileges required.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Platform Dashboard
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!stats) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="info">No platform statistics available.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="600" gutterBottom>
          Platform Administration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor and manage all tenants across the Equity Engine platform
        </Typography>
      </Box>

      {/* Platform Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Tenants"
            value={stats.platform.totalTenants}
            icon={<Domain />}
            color={theme.palette.primary.main}
            chips={
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<ActiveIcon />}
                  label={`${stats.platform.activeTenants} Active`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<TrialIcon />}
                  label={`${stats.platform.trialTenants} Trial`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Stack>
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Monthly Recurring Revenue"
            value={`$${stats.platform.totalMRR.toLocaleString()}`}
            icon={<MoneyIcon />}
            color={theme.palette.success.main}
            subtitle={`Avg: $${Math.round(stats.platform.averageTenantValue).toLocaleString()} per tenant`}
            trend={15}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Users"
            value={stats.aggregate.totalUsers.toLocaleString()}
            icon={<PeopleIcon />}
            color={theme.palette.info.main}
            subtitle="Across all tenants"
            trend={8}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Properties"
            value={stats.aggregate.totalProperties}
            icon={<AccountTree />}
            color={theme.palette.warning.main}
            subtitle={`${stats.aggregate.totalUnits.toLocaleString()} total units`}
            trend={12}
          />
        </Grid>
      </Grid>

      {/* Aggregate Statistics and Tenant Distribution */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="500" gutterBottom>
              Platform-Wide Metrics
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="600">
                    {stats.aggregate.totalReservations.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Reservations
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="success.main" fontWeight="600">
                    ${Math.round(stats.aggregate.monthlyRevenue / 1000)}K
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Revenue
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="info.main" fontWeight="600">
                    {stats.aggregate.averageOccupancy}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Occupancy
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="warning.main" fontWeight="600">
                    ${Math.round(stats.aggregate.totalRevenue / 1000)}K
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight="500" gutterBottom>
              Tenant Distribution
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Stack spacing={2}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box display="flex" alignItems="center">
                      <ActiveIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                      <Typography variant="body2">Active Tenants</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="600">
                      {stats.platform.activeTenants} ({Math.round((stats.platform.activeTenants / stats.platform.totalTenants) * 100)}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(stats.platform.activeTenants / stats.platform.totalTenants) * 100}
                    color="success"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box display="flex" alignItems="center">
                      <TrialIcon sx={{ fontSize: 16, mr: 1, color: 'info.main' }} />
                      <Typography variant="body2">Trial Tenants</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="600">
                      {stats.platform.trialTenants} ({Math.round((stats.platform.trialTenants / stats.platform.totalTenants) * 100)}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(stats.platform.trialTenants / stats.platform.totalTenants) * 100}
                    color="info"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box display="flex" alignItems="center">
                      <SuspendedIcon sx={{ fontSize: 16, mr: 1, color: 'error.main' }} />
                      <Typography variant="body2">Suspended Tenants</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="600">
                      {stats.platform.suspendedTenants} ({Math.round((stats.platform.suspendedTenants / stats.platform.totalTenants) * 100)}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(stats.platform.suspendedTenants / stats.platform.totalTenants) * 100}
                    color="error"
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="500" gutterBottom>
              Recent Tenants
            </Typography>
            <List sx={{ pt: 1 }}>
              {stats.recentTenants.map((tenant, index) => (
                <React.Fragment key={tenant._id}>
                  <ListItem 
                    alignItems="flex-start" 
                    sx={{ 
                      px: 0,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        borderRadius: 1,
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        sx={{ 
                          bgcolor: tenant.status === 'active' ? alpha(theme.palette.success.main, 0.1) :
                                   tenant.status === 'trial' ? alpha(theme.palette.info.main, 0.1) :
                                   tenant.status === 'suspended' ? alpha(theme.palette.error.main, 0.1) :
                                   alpha(theme.palette.grey[500], 0.1),
                          color: tenant.status === 'active' ? theme.palette.success.main :
                                 tenant.status === 'trial' ? theme.palette.info.main :
                                 tenant.status === 'suspended' ? theme.palette.error.main :
                                 theme.palette.grey[500],
                        }}
                      >
                        {getStatusIcon(tenant.status)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight="500">
                          {tenant.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={tenant.status.charAt(0).toUpperCase() + tenant.status.slice(1)}
                            size="small"
                            color={getStatusColor(tenant.status)}
                            sx={{ mr: 1, height: 20 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(tenant.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < stats.recentTenants.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Last Updated */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {new Date(stats.lastUpdated).toLocaleString()}
        </Typography>
      </Box>
    </Container>
  );
};

export default PlatformDashboard;